import { useEffect, useState } from 'react'
import {
  Plus, Trash2, Lock, Unlock, RefreshCw, CalendarDays, Clock,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import AdminLayout from '@/components/admin/AdminLayout'
import Button from '@/components/ui/Button'

const TIME_OPTIONS = [
  '08:00', '09:00', '10:00', '11:00',
  '13:00', '14:00', '15:00', '16:00',
]

function fmt12(time24) {
  if (!time24) return ''
  const [h, m] = time24.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12  = h % 12 || 12
  return `${h12}:${String(m).padStart(2,'0')} ${ampm}`
}

function dateLabel(isoDate) {
  return new Date(isoDate + 'T00:00:00').toLocaleDateString('en-PH', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  })
}

// Group slots by date
function groupByDate(slots) {
  const map = {}
  for (const s of slots) {
    if (!map[s.available_date]) map[s.available_date] = []
    map[s.available_date].push(s)
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
}

export default function AdminSiteVisits() {
  const [slots,      setSlots]      = useState([])
  const [loading,    setLoading]    = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Add-slot form
  const [formDate,      setFormDate]      = useState('')
  const [formTime,      setFormTime]      = useState('')
  const [formMaxVisits, setFormMaxVisits] = useState(1)
  const [adding,        setAdding]        = useState(false)
  const [formError,     setFormError]     = useState('')

  const today = new Date().toISOString().split('T')[0]

  async function fetchSlots() {
    setLoading(true)
    const { data } = await supabase
      .from('site_visit_slots')
      .select('id, available_date, available_time, max_visits_this_slot, booked_count, is_blocked, created_at')
      .gte('available_date', today)
      .order('available_date', { ascending: true })
      .order('available_time', { ascending: true })
    setSlots(data ?? [])
    setLoading(false)
  }

  useEffect(() => { fetchSlots() }, []) // eslint-disable-line

  async function handleRefresh() {
    setRefreshing(true)
    await fetchSlots()
    setRefreshing(false)
  }

  async function handleAddSlot(e) {
    e.preventDefault()
    setFormError('')
    if (!formDate) { setFormError('Select a date.'); return }
    if (!formTime) { setFormError('Select a time.'); return }
    if (new Date(formDate + 'T00:00:00') < new Date(today + 'T00:00:00')) {
      setFormError('Date must be today or later.')
      return
    }
    // Check duplicate
    const dupe = slots.find(
      (s) => s.available_date === formDate && s.available_time.slice(0,5) === formTime
    )
    if (dupe) { setFormError('That slot already exists.'); return }

    setAdding(true)
    const { data, error } = await supabase
      .from('site_visit_slots')
      .insert({
        available_date:       formDate,
        available_time:       formTime + ':00',
        max_visits_this_slot: formMaxVisits,
        booked_count:         0,
        is_blocked:           false,
      })
      .select()
      .single()

    if (error) {
      setFormError('Could not add slot. Try again.')
    } else {
      setSlots((prev) => {
        const next = [...prev, data]
        next.sort((a, b) =>
          a.available_date.localeCompare(b.available_date) ||
          a.available_time.localeCompare(b.available_time)
        )
        return next
      })
      setFormDate('')
      setFormTime('')
      setFormMaxVisits(1)
    }
    setAdding(false)
  }

  async function toggleBlock(slot) {
    const newVal = !slot.is_blocked
    await supabase
      .from('site_visit_slots')
      .update({ is_blocked: newVal })
      .eq('id', slot.id)
    setSlots((prev) =>
      prev.map((s) => s.id === slot.id ? { ...s, is_blocked: newVal } : s)
    )
  }

  async function deleteSlot(slot) {
    if (slot.booked_count > 0) return // safety — never delete a booked slot
    await supabase.from('site_visit_slots').delete().eq('id', slot.id)
    setSlots((prev) => prev.filter((s) => s.id !== slot.id))
  }

  const grouped = groupByDate(slots)

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-3xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">Site Visit Slots</h1>
            <p className="text-sm text-ink-muted mt-0.5">Manage available inspection dates for customers</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-ink-muted hover:text-ink-primary transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Add slot form */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-4 mb-6">
          <p className="text-sm font-semibold text-ink-primary mb-3 flex items-center gap-2">
            <Plus className="w-4 h-4 text-siglat-teal" />
            Add Available Slot
          </p>
          <form onSubmit={handleAddSlot} className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

              {/* Date */}
              <div>
                <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1">Date</label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  min={today}
                  className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal"
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1">Time</label>
                <select
                  value={formTime}
                  onChange={(e) => setFormTime(e.target.value)}
                  className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal"
                >
                  <option value="">Pick time</option>
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>{fmt12(t)}</option>
                  ))}
                </select>
              </div>

              {/* Max visits */}
              <div>
                <label className="block text-[10px] text-ink-muted uppercase tracking-widest mb-1">Max Visits</label>
                <select
                  value={formMaxVisits}
                  onChange={(e) => setFormMaxVisits(Number(e.target.value))}
                  className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal"
                >
                  {[1,2,3,4,5].map((n) => (
                    <option key={n} value={n}>{n} visit{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

            </div>

            {formError && (
              <p className="text-xs text-siglat-red">{formError}</p>
            )}

            <Button
              type="submit"
              size="sm"
              loading={adding}
              disabled={!formDate || !formTime}
              leftIcon={<Plus className="w-3.5 h-3.5" />}
            >
              Add Slot
            </Button>
          </form>
        </div>

        {/* Slots list */}
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1,2,3].map((n) => (
              <div key={n} className="h-16 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ))}
          </div>
        ) : grouped.length === 0 ? (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-10 text-center">
            <CalendarDays className="w-8 h-8 text-ink-muted mx-auto mb-2" />
            <p className="text-sm text-ink-muted">No upcoming slots. Add one above.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {grouped.map(([date, daySlots]) => (
              <div key={date}>

                {/* Date header */}
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays className="w-3.5 h-3.5 text-siglat-teal" />
                  <p className="text-xs font-semibold text-siglat-teal uppercase tracking-widest">
                    {dateLabel(date)}
                  </p>
                </div>

                {/* Time slots for this date */}
                <div className="flex flex-col gap-1.5">
                  {daySlots.map((slot) => {
                    const available = !slot.is_blocked && slot.booked_count < slot.max_visits_this_slot
                    const fullyBooked = !slot.is_blocked && slot.booked_count >= slot.max_visits_this_slot
                    return (
                      <div
                        key={slot.id}
                        className={`bg-surface-raised border rounded-xl px-4 py-3 flex items-center justify-between gap-3
                          ${slot.is_blocked ? 'border-siglat-red/25 opacity-60' :
                            fullyBooked    ? 'border-surface-border opacity-70' :
                                             'border-surface-border'}`}
                      >
                        <div className="flex items-center gap-3">
                          <Clock className="w-3.5 h-3.5 text-ink-muted shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-ink-primary">
                              {fmt12(slot.available_time?.slice(0,5))}
                            </p>
                            <p className="text-xs text-ink-muted">
                              {slot.booked_count}/{slot.max_visits_this_slot} booked
                              {slot.is_blocked && ' \u00b7 Blocked'}
                              {fullyBooked && ' \u00b7 Full'}
                              {available  && ' \u00b7 Available'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* Block/unblock */}
                          <button
                            type="button"
                            onClick={() => toggleBlock(slot)}
                            title={slot.is_blocked ? 'Unblock slot' : 'Block slot'}
                            className="p-1.5 rounded-lg text-ink-muted hover:text-ink-primary hover:bg-surface-overlay transition-colors"
                          >
                            {slot.is_blocked
                              ? <Unlock className="w-3.5 h-3.5" />
                              : <Lock className="w-3.5 h-3.5" />
                            }
                          </button>

                          {/* Delete (only if no bookings) */}
                          {slot.booked_count === 0 && (
                            <button
                              type="button"
                              onClick={() => deleteSlot(slot)}
                              title="Delete slot"
                              className="p-1.5 rounded-lg text-ink-muted hover:text-siglat-red hover:bg-siglat-red/10 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </AdminLayout>
  )
}
